import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenderAPI, clientsAPI } from '../utils/api';
import { ArrowLeft, ExternalLink, Clock, TrendingUp, Loader2, Check, X, FileText, Trash2, Copy } from 'lucide-react';

function TenderDetail() {
  var params = useParams();
  var navigate = useNavigate();
  var s1 = useState(null); var tender = s1[0]; var setTender = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(false); var showPromote = s3[0]; var setShowPromote = s3[1];
  var s4 = useState(false); var updating = s4[0]; var setUpdating = s4[1];
  var s5 = useState(null); var error = s5[0]; var setError = s5[1];

  useEffect(function() { fetchTender(); }, [params.id]);

  var fetchTender = async function() {
    try {
      var res = await tenderAPI.get(params.id);
      setTender(res.data);
    } catch (e) { setError('Failed to load tender'); }
    finally { setLoading(false); }
  };

  var handleStatusChange = async function(newStatus) {
    setUpdating(true);
    try {
      await tenderAPI.update(params.id, { status: newStatus });
      await fetchTender();
    } catch (e) { setError('Failed to update'); }
    finally { setUpdating(false); }
  };

  var handleDismiss = async function() {
    setUpdating(true);
    try {
      await tenderAPI.dismiss(params.id, { reason: 'Not relevant' });
      await fetchTender();
    } catch (e) { setError('Failed to dismiss'); }
    finally { setUpdating(false); }
  };

  var handleAIScore = async function() {
    setUpdating(true);
    try {
      await tenderAPI.aiScore(params.id);
      await fetchTender();
    } catch (e) { setError('AI scoring failed'); }
    finally { setUpdating(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (error && !tender) return <div className="p-6 text-red-600">{error}</div>;
  if (!tender) return null;

  var scoreCls = tender.final_score >= 4 ? 'bg-green-100 text-green-700' : tender.final_score >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
  var statusMap = { new: 'bg-blue-100 text-blue-700', reviewed: 'bg-slate-100 text-slate-600', pursuing: 'bg-primary/10 text-primary', preparing: 'bg-indigo-100 text-indigo-700', submitted: 'bg-purple-100 text-purple-700', won: 'bg-green-100 text-green-700', lost: 'bg-red-100 text-red-600', expired: 'bg-slate-100 text-slate-400' };
  var statusCls = statusMap[tender.status] || 'bg-slate-100 text-slate-600';

  var logItems = [];
  var logs = tender.decision_log || [];
  for (var i = logs.length - 1; i >= 0 && i >= logs.length - 10; i--) {
    var log = logs[i];
    logItems.push(
      <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
        <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0"></div>
        <div><p className="text-sm text-slate-700">{log.detail || log.action}</p><p className="text-xs text-slate-400">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</p></div>
      </div>
    );
  }

  var kwItems = [];
  var kws = tender.matched_keywords || [];
  for (var k = 0; k < kws.length; k++) {
    kwItems.push(<span key={k} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">{kws[k]}</span>);
  }

  return (
    <div className="p-6 space-y-6" data-testid="tender-detail-page">
      <div className="flex items-center gap-3">
        <button onClick={function() { navigate('/tender-scout/tenders'); }} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">{tender.title}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={'text-xs font-bold px-2 py-1 rounded ' + scoreCls}>Score: {tender.final_score}/5</span>
            <span className={'text-xs px-2 py-1 rounded ' + statusCls}>{tender.status}</span>
            {tender.reference && <span className="text-xs text-slate-400">Ref: {tender.reference}</span>}
            <span className="text-xs text-slate-400">Source: {tender.source_name}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Tender Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-xs text-slate-500">Organisation</p><p className="text-sm font-medium text-slate-900">{tender.organisation || '-'}</p></div>
              <div><p className="text-xs text-slate-500">Category</p><p className="text-sm text-slate-900">{tender.category || '-'}</p></div>
              <div><p className="text-xs text-slate-500">Closing Date</p><p className="text-sm text-slate-900">{tender.closing_date ? new Date(tender.closing_date).toLocaleDateString() : '-'}</p></div>
              <div><p className="text-xs text-slate-500">Estimated Value</p><p className="text-sm font-medium text-slate-900">{tender.estimated_value_myr ? 'RM ' + Number(tender.estimated_value_myr).toLocaleString() : tender.estimated_value_raw || '-'}</p></div>
            </div>
            {tender.description && <div><p className="text-xs text-slate-500 mb-1">Description</p><p className="text-sm text-slate-700">{tender.description}</p></div>}
            {tender.url && <a href={tender.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline"><ExternalLink size={14} /> View Original</a>}
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Scoring</h3>
            <div className="grid grid-cols-3 gap-4">
              <div><p className="text-xs text-slate-500">Keyword Score</p><p className="text-lg font-bold text-slate-900">{tender.score || 0}/5</p></div>
              <div><p className="text-xs text-slate-500">AI Score</p><p className="text-lg font-bold text-slate-900">{tender.ai_score || '-'}</p></div>
              <div><p className="text-xs text-slate-500">Final Score</p><p className="text-lg font-bold text-primary">{tender.final_score || 0}/5</p></div>
            </div>
            {tender.score_reason && <p className="text-xs text-slate-500">Reason: {tender.score_reason}</p>}
            {tender.ai_score_reason && <p className="text-xs text-slate-500">AI: {tender.ai_score_reason}</p>}
            {kwItems.length > 0 && <div className="flex flex-wrap gap-1">{kwItems}</div>}
          </div>

          {logItems.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Decision Log</h3>
              {logItems}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Actions</h3>
            {!tender.deal_id && tender.status !== 'not_relevant' && (
              <button onClick={function() { setShowPromote(true); }} className="w-full py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 flex items-center justify-center gap-2" data-testid="promote-to-deal-btn"><TrendingUp size={16} /> Promote to Deal</button>
            )}
            {tender.deal_id && (
              <button onClick={function() { navigate('/deals'); }} className="w-full py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm flex items-center justify-center gap-2"><Check size={16} /> View Linked Deal</button>
            )}
            <button onClick={handleAIScore} disabled={updating} className="w-full py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-score-btn">{updating ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />} AI Score</button>
            {tender.status === 'new' && <button onClick={function() { handleStatusChange('reviewed'); }} disabled={updating} className="w-full py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50">Mark as Reviewed</button>}
            {(tender.status === 'new' || tender.status === 'reviewed') && <button onClick={function() { handleStatusChange('pursuing'); }} disabled={updating} className="w-full py-2 border border-primary text-primary rounded-lg text-sm hover:bg-primary/5 disabled:opacity-50">Start Pursuing</button>}
            {tender.status !== 'not_relevant' && <button onClick={handleDismiss} disabled={updating} className="w-full py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-2"><Trash2 size={14} /> Dismiss</button>}
          </div>

          {tender.notes && (
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Notes</h3>
              <p className="text-sm text-slate-600">{tender.notes}</p>
            </div>
          )}
        </div>
      </div>

      {showPromote && <PromoteModal tender={tender} onClose={function() { setShowPromote(false); }} onSuccess={function() { setShowPromote(false); fetchTender(); }} />}
    </div>
  );
}

function PromoteModal({ tender, onClose, onSuccess }) {
  var s1 = useState(tender.title || ''); var title = s1[0]; var setTitle = s1[1];
  var s2 = useState(tender.organisation || ''); var clientName = s2[0]; var setClientName = s2[1];
  var s3 = useState('Lead'); var stage = s3[0]; var setStage = s3[1];
  var s4 = useState(false); var saving = s4[0]; var setSaving = s4[1];
  var s5 = useState(null); var err = s5[0]; var setErr = s5[1];

  var handlePromote = async function() {
    setSaving(true);
    setErr(null);
    try {
      await tenderAPI.promote(tender._id, {
        project_title: title,
        client_name: clientName,
        stage: stage,
      });
      onSuccess();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Promotion failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="promote-modal">
      <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Promote to Deal</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
        <div className="space-y-3">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Project Title</label><input type="text" value={title} onChange={function(e) { setTitle(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="promote-title-input" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Client / Organisation</label><input type="text" value={clientName} onChange={function(e) { setClientName(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="promote-client-input" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Initial Stage</label>
            <select value={stage} onChange={function(e) { setStage(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
              <option value="Lead">Lead</option><option value="Proposal Development">Proposal Development</option>
            </select>
          </div>
          {tender.estimated_value_myr && <p className="text-sm text-slate-500">Estimated Value: RM {Number(tender.estimated_value_myr).toLocaleString()}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
          <button onClick={handlePromote} disabled={saving || !title.trim()} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-promote-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Create Deal</button>
        </div>
      </div>
    </div>
  );
}

export default TenderDetail;
